import nprogress from 'nprogress';
import { pathOr } from 'ramda';
import { call, put, take, fork, spawn, race } from 'redux-saga/effects';

import { createStandaloneToast, theme } from '@frontend/legacy-components';

import { initializeLogin } from 'modules/auth/login/data/sagas';

import { capitalize, getErrorMessage } from 'utils/stringUtils';

import * as actions from './actions';
import * as types from './types';

const { toast } = createStandaloneToast({ theme });

nprogress.configure({
  showSpinner: false,
  minimum: 0.25,
  speed: 500,
  trickleSpeed: 200,
});

export default function* root() {
  yield fork(initializeApp);
  yield spawn(watchNotifications);
  yield spawn(watchProgressBar);
  yield spawn(watchScrollRestoration);
  yield spawn(watchPagePending);
}

export function* initializeApp() {
  try {
    yield call(initializeLogin);
    yield put(actions.initializeSuccess());
  } catch (error) {
    yield put(actions.page.error({ error }));
  }
}

export function* watchNotifications() {
  for (;;) {
    const action = yield take((action) => (action.meta || {}).__NOTIFY__);
    const opts = action.meta.__NOTIFY__;
    const title = opts.title || capitalize(opts.status);
    const message = getErrorMessage(opts.message);
    const duration = opts.duration === null ? null : undefined; // allow setting duration to null - toast notification to remain open
    yield call(toast, {
      status: opts.status,
      title: title,
      description: message,
      duration: duration,
    });
  }
}

function* watchProgressBar() {
  for (;;) {
    yield take(types.PAGE_REQUEST);
    yield call(nprogress.start);
    yield race({
      ok: take(types.PAGE_SUCCESS),
      error: take(types.PAGE_ERROR),
    });
    yield call(nprogress.done);
  }
}

function* watchScrollRestoration() {
  const COMPLETELY_LOADED_TYPES = [types.PAGE_SUCCESS, types.PAGE_ERROR];
  for (;;) {
    const { payload } = yield take(types.PAGE_REQUEST);
    const winner = yield race({
      completelyLoaded: take(COMPLETELY_LOADED_TYPES),
      prematurelyLoaded: take(types.SET_IS_LOADED),
    });
    if (pathOr(false, ['location', 'state', 'noScrollRestoration'], payload)) {
      const scrollRoot = document.getElementById('scrollRoot');
      if (scrollRoot) {
        scrollRoot.scrollTop = 0;
      }
    }
    if (winner.prematurelyLoaded) {
      yield take(COMPLETELY_LOADED_TYPES);
    }
  }
}

function* watchPagePending() {
  for (let counter = 0; ; ) {
    const action = yield take();
    if (action.type === types.PAGE_PENDING_START) {
      if (!counter) {
        window.onbeforeunload = () => true;
      }
      counter++;
    } else if (action.type === types.PAGE_PENDING_FINISH) {
      if (counter > 0) {
        counter--;
      }
      if (!counter) {
        window.onbeforeunload = undefined;
      }
    }
  }
}
